import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { RARITY_META, type CollectState } from '@goodtime/shared';
import { boardLines, RELICS_EXPLAINER, RELICS_NAME, type Relic } from '../../logic/collect/relics';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';
import { MISSING_ALPHA, MISSING_TINT, type RelicShelfArt } from './RelicShelf';

interface Tile {
  root: Container;
  back: Graphics;
  glow: Sprite;
  relic: Sprite;
  bezel: Sprite;
  name: Text;
  rarity: Text;
}

const TILE = 76;

/**
 * The relics board, opened by a tap on the shelf (the one-tap
 * explanation): the slot's three relics with their names, lit when found
 * and a dark shape until then, what the set pays, the sets found here so
 * far, and what relics are, in plain words. CLOSE or a tap on the dim
 * closes it.
 */
export class RelicsBoard extends BoardDialog {
  private readonly tiles: Tile[] = [];
  private readonly prizeIcon = new Sprite();
  private readonly prize: Text;
  private readonly sets: Text;
  private readonly explain: Text;
  private readonly closeButton: BrassButton;
  private relics: Relic[] = [];
  private state: CollectState | null = null;
  private done: (() => void) | null = null;

  constructor(
    private readonly art: RelicShelfArt & { coin: () => Texture | null; gem: () => Texture | null },
  ) {
    super({ title: RELICS_NAME.toUpperCase(), floor: art.floor(), maxWidth: 380, titleSize: 18 });
    for (let i = 0; i < 3; i++) this.tiles.push(this.makeTile());
    this.prizeIcon.anchor.set(0.5);
    this.prize = new Text({ text: '', style: displayStyle(19) });
    this.prize.anchor.set(0, 0.5);
    this.sets = new Text({ text: '', style: uiStyle(14, COLOURS.cream, '600') });
    this.sets.anchor.set(0.5, 0);
    this.explain = new Text({ text: RELICS_EXPLAINER, style: { ...uiStyle(14, COLOURS.cream, '500'), align: 'center', wordWrap: true } });
    this.explain.anchor.set(0.5, 0);
    this.closeButton = new BrassButton({ label: 'CLOSE', kind: 'wood', size: 16, height: 42, onTap: () => this.finish() });
    this.content.addChild(this.prizeIcon, this.prize, this.sets, this.explain, this.closeButton);
    this.onDimTap = () => this.finish();
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): {
    open: boolean;
    title: string;
    relics: { name: string; found: boolean }[];
    prize: string;
    sets: string;
    explain: string;
    buttons: string[];
  } {
    return {
      open: this.visible,
      title: this.titleText,
      relics: this.relics.map((r, i) => ({ name: r.name, found: this.state?.pieces[i] === true })),
      prize: this.prize.text,
      sets: this.sets.text,
      explain: this.explain.text,
      buttons: this.visible ? [this.closeButton.labelText] : [],
    };
  }

  /** Press a button by its label (browser tests). */
  press(label: string): boolean {
    if (!this.visible || label !== this.closeButton.labelText) return false;
    this.finish();
    return true;
  }

  /** Show the slot's relics; resolves when the board has closed. */
  async play(relics: Relic[], state: CollectState): Promise<void> {
    this.relics = relics;
    this.state = state;
    this.setFloor(this.art.floor());
    const lines = boardLines(state);
    this.prize.text = lines.prize;
    this.sets.text = lines.sets;
    const gems = state.prize.gems > 0 && state.prize.coins <= 0;
    const icon = gems ? this.art.gem() : this.art.coin();
    if (icon) this.prizeIcon.texture = icon;
    this.prizeIcon.visible = icon !== null;
    this.tiles.forEach((tile, i) => this.paintTile(tile, relics[i], state.pieces[i] === true));
    this.arrange();
    const gone = new Promise<void>((resolve) => (this.done = resolve));
    await this.open();
    return gone;
  }

  layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  destroy(options?: Parameters<BoardDialog['destroy']>[0]): void {
    this.done = null;
    super.destroy(options);
  }

  private makeTile(): Tile {
    const root = new Container();
    const back = new Graphics();
    const glow = new Sprite(glowTexture());
    const relic = new Sprite();
    const bezel = new Sprite();
    for (const s of [glow, relic, bezel]) s.anchor.set(0.5);
    glow.tint = 0xffd24a;
    glow.blendMode = 'add';
    bezel.tint = 0xf2e2c0;
    const name = new Text({ text: '', style: { ...displayStyle(14, COLOURS.gold), align: 'center', wordWrap: true } });
    name.anchor.set(0.5, 0);
    const rarity = new Text({ text: '', style: uiStyle(12, COLOURS.cream, '600') });
    rarity.anchor.set(0.5, 0);
    root.addChild(back, glow, relic, bezel, name, rarity);
    this.content.addChild(root);
    return { root, back, glow, relic, bezel, name, rarity };
  }

  private paintTile(tile: Tile, relic: Relic | undefined, found: boolean): void {
    tile.root.visible = relic !== undefined;
    if (!relic) return;
    const texture = this.art.relic(relic.art);
    if (texture) tile.relic.texture = texture;
    tile.relic.visible = texture !== null;
    const box = TILE * 0.66 * 1.32;
    tile.relic.scale.set(texture ? box / Math.max(1, texture.width, texture.height) : 1);
    tile.relic.tint = found ? 0xffffff : MISSING_TINT;
    tile.relic.alpha = found ? 1 : MISSING_ALPHA;
    tile.glow.alpha = found ? 0.45 : 0;
    tile.name.text = relic.name;
    tile.rarity.text = RARITY_META[relic.rarity].label;
    const bezel = this.art.bezel();
    if (bezel) tile.bezel.texture = bezel;
    tile.bezel.visible = bezel !== null;
  }

  private arrange(): void {
    if (!this.closeButton) return;
    const w = this.contentWidth;
    const col = w / 3;
    let y = 6;
    this.tiles.forEach((tile, i) => {
      tile.root.position.set(col * i + col / 2, y + TILE / 2);
      tile.back.clear().circle(0, 0, TILE * 0.38).fill({ color: 0x140a03, alpha: 0.85 });
      tile.glow.width = tile.glow.height = TILE * 1.4;
      tile.bezel.width = tile.bezel.height = TILE;
      tile.name.style.wordWrapWidth = col - 6;
      tile.name.position.set(0, TILE / 2 + 6);
      tile.rarity.position.set(0, tile.name.y + tile.name.height + 1);
    });
    const tallest = Math.max(...this.tiles.map((t) => t.rarity.y + t.rarity.height));
    y += TILE / 2 + tallest + 14;
    // The prize line, centred with its icon.
    const iconSize = 26;
    this.prizeIcon.width = this.prizeIcon.height = iconSize;
    const rowW = (this.prizeIcon.visible ? iconSize + 8 : 0) + this.prize.width;
    const left = (w - rowW) / 2;
    this.prizeIcon.position.set(left + iconSize / 2, y + iconSize / 2);
    this.prize.position.set(left + (this.prizeIcon.visible ? iconSize + 8 : 0), y + iconSize / 2);
    y += iconSize + 8;
    this.sets.position.set(w / 2, y);
    y += this.sets.height + 10;
    this.explain.style.wordWrapWidth = w - 8;
    this.explain.position.set(w / 2, y);
    y += this.explain.height + 14;
    const bw = Math.min(180, w);
    this.closeButton.resize(bw, 42);
    this.closeButton.position.set((w - bw) / 2, y);
    y += 42 + 4;
    if (Math.abs(y - this.lastH) > 1) {
      this.lastH = y;
      this.setContentHeight(y);
    }
  }

  private lastH = 0;

  private finish(): void {
    const done = this.done;
    this.done = null;
    if (!done) return;
    void this.close().then(done);
  }
}
